















































import { Component, Vue } from 'vue-property-decorator';
import get from 'lodash/get';

type ImpPosition = 'top-right' | 'bottom-left';

const OPEN_DELAY_MS = 420;
const PROXIMITY_PX = 90;
const STORAGE_KEY = 'impersonate-indicator-pos';
const QUERY_KEY = 'impersonate-pos';

@Component({})
export default class ImpersonateIndicator extends Vue {
  isOpen = false;
  private openTimer: ReturnType<typeof setTimeout> | null = null;
  private pressed = false;
  private isTouch = false;
  private onMoveBound: ((e: MouseEvent) => void) | null = null;
  private onDownBound: (() => void) | null = null;
  private onUpBound: (() => void) | null = null;
  private onDocClickBound: ((e: MouseEvent) => void) | null = null;

  get position(): ImpPosition {
    if (this.$mobileCheck()) {
      return 'bottom-left';
    }
    const fromQuery = new URLSearchParams(window.location.search).get(
      QUERY_KEY
    );
    if (fromQuery === 'top-right' || fromQuery === 'bottom-left') {
      window.localStorage.setItem(STORAGE_KEY, fromQuery);
      return fromQuery;
    }
    const fromStorage = window.localStorage.getItem(STORAGE_KEY);
    if (fromStorage === 'top-right' || fromStorage === 'bottom-left') {
      return fromStorage;
    }
    return 'top-right';
  }

  get positionClass() {
    return this.position === 'top-right' ? 'pos-tr' : 'pos-bl';
  }

  get businessInfo() {
    const info = this.$store.getters['business/getBusinessInfo'];
    return (info && info.business) || {};
  }

  get businessName() {
    return get(this.businessInfo, 'name', '');
  }

  get fullname() {
    return get(this.businessInfo, 'loggedInManager.fullname', 'Super Admin');
  }

  get businessLogo() {
    return get(this.businessInfo, 'logo.publicPath.large', '');
  }

  get businessInitial() {
    return (this.businessName || '?').charAt(0).toUpperCase();
  }

  get circleAriaLabel() {
    return this.isOpen
      ? 'Close impersonate menu'
      : 'Impersonating ' + this.businessName + '. Open impersonate menu';
  }

  mounted() {
    this.isTouch =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: none)').matches;

    if (this.isTouch) {
      this.onDocClickBound = this.onDocClick.bind(this);
      document.addEventListener('click', this.onDocClickBound);
      return;
    }

    this.onMoveBound = this.onMove.bind(this);
    this.onDownBound = this.onDown.bind(this);
    this.onUpBound = this.onUp.bind(this);
    document.addEventListener('mousemove', this.onMoveBound);
    document.addEventListener('mousedown', this.onDownBound);
    document.addEventListener('mouseup', this.onUpBound);
  }

  beforeDestroy() {
    this.clearTimer();
    if (this.onMoveBound) {
      document.removeEventListener('mousemove', this.onMoveBound);
    }
    if (this.onDownBound) {
      document.removeEventListener('mousedown', this.onDownBound);
    }
    if (this.onUpBound) {
      document.removeEventListener('mouseup', this.onUpBound);
    }
    if (this.onDocClickBound) {
      document.removeEventListener('click', this.onDocClickBound);
    }
  }

  private clearTimer() {
    if (this.openTimer !== null) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
  }

  private cornerXY() {
    return this.position === 'top-right'
      ? { x: window.innerWidth, y: 0 }
      : { x: 0, y: window.innerHeight };
  }

  private isInteractive(el: Element | null): boolean {
    if (!el) {
      return false;
    }
    if (el.closest('[data-impersonate-circle]')) {
      return false;
    }
    return !!el.closest('button, a, input, select, textarea, [data-no-dock]');
  }

  private isWithinIndicator(x: number, y: number): boolean {
    const root = this.$el as Element;
    const circle = root.querySelector('[data-impersonate-circle]');
    const panel = root.querySelector('[data-impersonate-panel]');
    const margin = 16;
    let left = Infinity;
    let top = Infinity;
    let right = -Infinity;
    let bottom = -Infinity;
    const els = [circle, panel];
    for (let i = 0; i < els.length; i++) {
      const el = els[i];
      if (!el) {
        continue;
      }
      const r = el.getBoundingClientRect();
      if (r.left < left) {
        left = r.left;
      }
      if (r.top < top) {
        top = r.top;
      }
      if (r.right > right) {
        right = r.right;
      }
      if (r.bottom > bottom) {
        bottom = r.bottom;
      }
    }
    if (left === Infinity) {
      return false;
    }
    return (
      x >= left - margin &&
      x <= right + margin &&
      y >= top - margin &&
      y <= bottom + margin
    );
  }

  private onMove(e: MouseEvent) {
    if (this.isOpen) {
      if (this.isWithinIndicator(e.clientX, e.clientY)) {
        return;
      }
      this.clearTimer();
      this.isOpen = false;
      return;
    }

    const corner = this.cornerXY();
    const dx = e.clientX - corner.x;
    const dy = e.clientY - corner.y;
    const near = Math.sqrt(dx * dx + dy * dy) <= PROXIMITY_PX;
    if (!near) {
      this.clearTimer();
      return;
    }

    const under = document.elementFromPoint(e.clientX, e.clientY);
    if (this.isInteractive(under) || this.pressed) {
      this.clearTimer();
      return;
    }

    if (this.openTimer === null) {
      this.openTimer = setTimeout(() => {
        this.openTimer = null;
        this.isOpen = true;
      }, OPEN_DELAY_MS);
    }
  }

  private onDown() {
    this.pressed = true;
    this.clearTimer();
  }

  private onUp() {
    this.pressed = false;
  }

  private onDocClick(e: MouseEvent) {
    if (!this.isOpen) {
      return;
    }
    const target = e.target as Element | null;
    if (target && target.closest('[data-impersonate-root]')) {
      return;
    }
    this.isOpen = false;
  }

  onCircleClick() {
    if (this.isTouch) {
      this.isOpen = !this.isOpen;
    }
  }

  disableImpersonate() {
    window.location.assign('/admin/businesses?disable-impersonate=1');
  }
}
